// ============================================================================
// Prime is not running (2026-10-06): what /agents shows when no Prime session
// is live: never started, ended, failed or filed away. Before this the page
// went quietly to the quick chat, so on a phone Prime looked as if it did not
// exist, and starting it meant finding the Agents list or Settings. One button
// starts it (its memory notes and the board come with it), the last session
// stays readable, the quick chat is a step aside.
// A session that is only idle (Suspended) is live: its thread wakes it.
// Since Prime is the lead (2026-10-10, lib/lead.ts) every typed "Ask Prime"
// lands here when it is not running, with what was typed in `ask`: the words
// stay in a box, go into Prime's composer once it starts (unsent), and ride
// along to the quick chat, so nothing typed is lost. With the factory off
// there is nothing to start, so the screen says so and keeps the quick chat.
// ============================================================================
import { useState } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import { PrimeMark } from '@/components/PrimeMark'
import { api, engine } from '@/runtime'
import { useAllRuns } from '@/db/hooks'
import { isLead } from '@/kinds/agentrun/components'
import { useNow } from '@/hooks/useNow'
import { fmtAgo } from '@/lib/time'
import { QUICK_CHAT } from '@/lib/lead'
import { Button } from './ui'

export const PrimeAsleep = ({ ask, factory = true }: { ask?: string; factory?: boolean }) => {
  const navigate = useNavigate()
  const now = useNow(60_000)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [words, setWords] = useState(ask ?? '')
  const kept = words.trim() ? { ask: words } : {}
  const last = (useAllRuns() ?? [])
    .filter((r) => isLead(r) && !r.deleted_at)
    .sort((a, b) => (a.created_at < b.created_at ? 1 : -1))[0]
  const start = async () => {
    setBusy(true)
    setError('')
    try {
      const st = await api.leadStart()
      await engine.sync()
      void navigate({ to: '/agents/run/$id', params: { id: st.run_id }, search: kept as never })
    } catch (e) {
      setError((e as Error).message || 'Prime did not start')
      setBusy(false)
    }
  }
  const when = last?.spec.ended_at || last?.spec.last_active_at || last?.spec.updated_at
  return (
    <div
      className="flex flex-col items-center text-center pt-10 pb-6 px-2 fade-in"
      data-testid="prime-asleep"
    >
      <span className="h-14 w-14 rounded-2xl bg-accent-soft text-accent-text flex items-center justify-center mb-4">
        <PrimeMark size={26} aria-hidden />
      </span>
      <h1 className="t-title2 mb-1.5">Prime isn't running</h1>
      <p className="text-sm text-muted max-w-80 mb-5">
        {factory
          ? 'Start it and it picks up where things are: its notes, the board, the agents and anything waiting on you.'
          : 'Prime runs on the software factory, which is not switched on here: Settings → Connectors.'}
      </p>
      {ask !== undefined && (
        <label className="w-full max-w-80 mb-4 text-left">
          <span className="label text-muted block mb-1 px-1">
            {factory ? 'For Prime once it starts' : 'What you wrote'}
          </span>
          <textarea
            value={words}
            onChange={(e) => setWords(e.target.value)}
            rows={3}
            className="w-full rounded-[var(--radius-card)] bg-panel border border-line px-3 py-2 text-[15px]"
            data-testid="prime-asleep-words"
          />
        </label>
      )}
      {factory && (
        <Button
          variant="primary"
          big
          className="w-full max-w-80"
          onClick={() => void start()}
          disabled={busy}
          data-testid="prime-asleep-start"
        >
          {busy ? 'Starting Prime…' : 'Start Prime'}
        </Button>
      )}
      {error && <p className="text-xs text-muted mt-2">{error}</p>}
      <div className="flex flex-col items-center gap-1 mt-4 text-sm">
        {last && (
          <Link
            to="/agents/run/$id"
            params={{ id: last.id }}
            className="text-muted hover:text-fg min-h-10 inline-flex items-center"
            data-testid="prime-asleep-last"
          >
            Read the last session{when ? ` (${last.spec.status.toLowerCase()} ${fmtAgo(when, now)})` : ''}
          </Link>
        )}
        <Link
          to="/agents"
          search={{ quick: true, ...kept } as never}
          className="text-muted hover:text-fg min-h-10 inline-flex items-center"
          data-testid="prime-asleep-quick"
        >
          {QUICK_CHAT} instead
        </Link>
      </div>
    </div>
  )
}
